import ArrowLongRightIcon from "@heroicons/react/24/solid/ArrowLongRightIcon";
import ClientSheetResponseResource from "common/resources/ClientSheet/ClientSheetResponseResource";
import Button, { EButtonVariant } from "components/elements/Button";
import { IPagination } from "components/elements/InfiniteScroll";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import I18n from "components/materials/I18n";
import ApplicationConfig from "configs/ApplicationConfig";
import { useCallback, useEffect, useRef, useState } from "react";
import ClientService from "services/ClientService";
import { container } from "tsyringe";
import classes from "./classes.module.scss";

const clientService = container.resolve(ClientService);
const configService = container.resolve(ApplicationConfig);

type IProps = {
	officeId: string;
	onSelectClient: (clientSheetId: string) => void;
};

export default function PhysicalPersonClients({ officeId, onSelectClient }: IProps) {
	const [rows, setRows] = useState<IRowProps[]>([]);
	const [count, setCount] = useState<{ count: number }>({ count: 0 });
	const pagination = useRef<IPagination>({ skip: 0, take: configService.get().defaultPagination });
	const search = useRef<string | null>(null);

	const buildRows = useCallback(
		(clients: ClientSheetResponseResource[]): IRowProps[] => {
			return clients.map((clientSheet) => {
				return {
					rowKey: clientSheet.id,
					civility: I18n.asset.enums.ECivility[clientSheet.civility],
					lastName: clientSheet.lastName,
					firstName: clientSheet.firstName,
					subscriptionSum: clientSheet.subscriptionSum,
					lastSubscriptionDate: clientSheet.lastSubscriptionDate,
					numberOfProductSubscribed: clientSheet.numberOfProductSubscribed,
					select: (
						<Button variant={EButtonVariant.TEXT} onClick={() => onSelectClient(clientSheet.id)}>
							{I18n.asset.common.select}
							<ArrowLongRightIcon className={classes["icon"]} />
						</Button>
					),
				};
			});
		},
		[onSelectClient],
	);

	const fetchClients = useCallback(
		async () =>
			clientService.getClientSheetsByOfficeId(officeId, pagination.current, search.current).then((clientSheets) => {
				if (clientSheets.length === 0) return [];

				setRows((_rows) => [..._rows, ...buildRows(clientSheets)]);
				pagination.current.skip += pagination.current.take;
				return clientSheets;
			}),
		[buildRows, officeId],
	);

	const onNext = useCallback(
		(release: () => void) => {
			fetchClients().then((clients) => {
				if (!clients.length) return console.warn("No more value to load");
				release();
			});
		},
		[fetchClients],
	);

	useEffect(() => {
		clientService.countByOfficeId(officeId).then(setCount);
	}, [officeId]);

	const onSearch = useCallback((searchParam: string) => {
		pagination.current.skip = 0;
		search.current = (searchParam && searchParam.trim()) || null;
		setRows([]);
	}, []);

	return <Table columnsHead={columsHead} rows={rows} count={count.count} placeholderSearchbar={I18n.trslt(I18n.asset.common.search)} onNext={onNext} onSearch={onSearch} />;
}

const columsHead: readonly TableColumn[] = [
	{
		headContent: I18n.asset.pages.clients.table.columns.civility,
		key: "civility",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.last_name,
		key: "lastName",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.first_name,
		key: "firstName",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.number_of_subscriptions,
		key: "numberOfProductSubscribed",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.subscription_sum,
		formatType: ECellType.EURO,
		key: "subscriptionSum",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.last_subscription_date,
		formatType: ECellType.DATE,
		key: "lastSubscriptionDate",
	},
	{
		headContent: "",
		key: "select",
	},
];
